'use client';

import React, { useContext } from 'react';
import { useBloodVision } from '../BloodVisionContext';
import { CustomImage as Image } from 'components/index';
import { ZipCodeContext } from './ZipCodeContext';
import { focusZipCodeInput } from './checkoutGuidance';
import styles from './FeatureList.module.css';

export const Divider = ({ pixelPadding }: { pixelPadding?: string }) => (
  <div
    className={styles.divider}
    style={
      pixelPadding
        ? ({ ['--divider-margin']: `${pixelPadding} 0` } as React.CSSProperties)
        : undefined
    }
  />
);

export const XLDivider = () => <div className={styles.xlDivider} />;

const FeatureList: React.FC = () => {
  const {
    plans,
    selectedPlan,
    selectPlanByUser,
    zipCode,
    isZipValid,
    hasUserProvidedZip,
  } = useBloodVision();
  const { zipCodeInputRef } = useContext(ZipCodeContext);

  const handlePlanSelect = (plan: any) => {
    selectPlanByUser(plan);
    if (!hasUserProvidedZip || !zipCode || zipCode.length !== 5 || !isZipValid) {
      focusZipCodeInput(zipCodeInputRef);
    }
  };

  // If we have plans from the API, show each plan as a separate FeatureList box
  if (plans && plans.length > 0) {
    return (
      <div className={styles.planWrapper}>
        {plans.map((plan, index) => {
          // Hardcoded to show the annual plan as popular
          const isPopular = plan?.show_tag;
          const isSelected = selectedPlan?.id === plan.id;
          const isZipReady =
            hasUserProvidedZip && !!zipCode && zipCode.length === 5 && isZipValid;
          const isDiscounted = plan.discount_applied;
          const pricingInfo = {
            monthlyPrice: plan.price_per_month,
            originalMonthlyPrice: plan.original_price_per_month,
            annualPrice: plan.price,
            originalAnnualPrice: plan.original_price,
          };

          const isCampaignPlan = plan.is_plan_campaign === true;

          const featuresToDisplay = plan.plan_details.map((detail) => ({
            title: detail.title,
            description: detail.description,
          }));

          const imgSrc =
            index === 0
              ? 'web_v2/blood-vision/icons/plans/head-0.png'
              : 'web_v2/blood-vision/icons/plans/head-1.png';

          return (
            <div
              className={styles.featureListContainer}
              key={plan.id}
              data-selected={isSelected}
              data-zip-ready={isZipReady}
              onClick={() => handlePlanSelect(plan)}
            >
              {isPopular && !isCampaignPlan ? (
                <div className={styles.popularTag}>POPULAR</div>
              ) : null}
              {isCampaignPlan ? (
                <div className={styles.popularTag}>LIMITED TIME</div>
              ) : null}
              <div className={styles.topSection}>
                <div className={styles.imageContainer}>
                  <Image
                    src={imgSrc}
                    alt={`${plan.name} Blood Vision plan`}
                    height="64"
                    width="48"
                  />
                  <div className={styles.textContainer}>
                    <h3 className={styles.head}>{plan.name}</h3>
                    <p className={styles.subhead}>{plan.subtitle}</p>
                  </div>
                </div>
                <div className={styles.pricingContainer}>
                  <p className={styles.yearlyPrice}>
                    {isDiscounted && (
                      <span
                        style={{
                          textDecoration: 'line-through',
                          marginRight: '4px',
                          color: '#808080',
                          fontSize: '14px',
                        }}
                      >
                        ${pricingInfo.originalAnnualPrice}
                      </span>
                    )}
                    {!isDiscounted && (
                      <span
                        style={{
                          fontSize: '16px',
                        }}
                      >
                        ${pricingInfo.annualPrice}
                      </span>
                    )}
                    {isDiscounted && (
                      <span
                        style={{
                          fontSize: '16px',
                        }}
                      >
                        ${pricingInfo.annualPrice}
                      </span>
                    )}
                  </p>
                  <p className={styles.planTenureTotalPrice}>
                    {pricingInfo.annualPrice > 0 ? (
                      <>
                        <span
                          style={{
                            color: '#808080',
                            fontSize: '14px',
                          }}
                        >
                          {/* GONFIG: changed for campaign - it was 1 earlier */}
                          {/* {plans.indexOf(plan) === 1
                            ? 'Charged annually at'
                            : 'Charged half-yearly at'}{' '} */}
                          {plan.name.toLocaleLowerCase().includes('annual')
                            ? 'Renews annually'
                            : 'Renews half-yearly'}{' '}
                          {/* {isDiscounted && (
                            <span
                              style={{
                                textDecoration: 'line-through',
                                marginRight: '4px',
                              }}
                            >
                              ${pricingInfo.originalAnnualPrice}
                            </span>
                          )}
                          {!isDiscounted && (
                            <span>${pricingInfo.annualPrice}</span>
                          )} */}
                        </span>
                        {/* {isDiscounted && (
                          <span>${pricingInfo.annualPrice}</span>
                        )} */}
                      </>
                    ) : (
                      'Blood Vision campaign discount applied'
                    )}
                  </p>
                </div>
              </div>
              <Divider pixelPadding={'16px'} />
              {featuresToDisplay.map((feature, idx) => (
                <div className={styles.feature} key={idx}>
                  <div className={styles.checkIcon} style={{ marginTop: '-2px' }}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className={styles.featureContent}>
                    <h4 className={styles.featureTitle}>{feature.title}</h4>
                    {/* {feature.description && (
                      <FeatureDescription>
                        {feature.description}
                      </FeatureDescription>
                    )} */}
                    {/* {!(idx === featuresToDisplay.length - 1) && (
                      <Divider pixelPadding={'12px'} />
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // No plans available - return null
  return null;
};

export default FeatureList;
